import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import EmptyState from '../components/common/EmptyState';
import StatusBadge from '../components/common/StatusBadge';
import { History, RefreshCw, Truck, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  completed: 'success', delivered: 'success', paid: 'success',
  in_transit: 'info', assigned: 'info', accepted: 'info', available: 'indigo',
  cancelled: 'danger', unknown: 'neutral',
};
const money = (v) => (v === null || v === undefined || v === '') ? '—' : `$${Number(v).toLocaleString()}`;
const fmtDate = (d) => { if (!d) return '—'; const dt = new Date(d); return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString(); };

export default function OperationalHistory() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({ entries: [], count: 0 });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/loads/history`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(res.status === 403 ? 'disabled' : 'Failed to load operational history.');
      setData(await res.json());
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const entries = data.entries || [];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="operational-history">
        <Breadcrumb items={[{ label: 'Operational History' }]} />
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
              <History className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Operational History</h1>
              <p className="text-sm text-gray-500 mt-0.5">Historical record of BlueLock freight loads and completed customer jobs. Read-only; internal quote economics are not shown here.</p>
            </div>
          </div>
          <button onClick={load} data-testid="ophist-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-900 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-gray-100"><EmptyState variant="no-records" title="Loading…" message="Fetching operational history." testid="ophist-loading" /></div>
        ) : error ? (
          <div className="bg-white rounded-xl border border-gray-100">
            <EmptyState variant={error === 'disabled' ? 'feature-disabled' : 'loading-failure'} testid="ophist-error" />
          </div>
        ) : entries.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100">
            <EmptyState variant="no-records" title="No operational history yet"
              message="Completed loads and accepted customer jobs will appear here as BlueLock performs work."
              testid="ophist-empty" />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200" data-testid="ophist-list">
            <div className="px-4 py-3 border-b border-gray-100 text-sm text-gray-500">{data.count} record{data.count === 1 ? '' : 's'} · sources: loads + accepted/completed jobs</div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm" data-testid="ophist-table">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="py-3 px-4">Reference</th>
                    <th className="py-3 px-4">Type</th>
                    <th className="py-3 px-4">Customer</th>
                    <th className="py-3 px-4">Route</th>
                    <th className="py-3 px-4">Service Date</th>
                    <th className="py-3 px-4">Revenue</th>
                    <th className="py-3 px-4">Payment</th>
                    <th className="py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={`${e.type}-${e.id}`} className="border-b hover:bg-gray-50" data-testid={`ophist-row-${e.id}`}>
                      <td className="py-3 px-4 font-medium text-gray-900">{e.reference}</td>
                      <td className="py-3 px-4">{e.type === 'load' ? <span className="inline-flex items-center gap-1 text-gray-600"><Truck className="w-4 h-4" />Load</span> : <span className="inline-flex items-center gap-1 text-gray-600"><Package className="w-4 h-4" />Job</span>}</td>
                      <td className="py-3 px-4">{e.customer}</td>
                      <td className="py-3 px-4 text-gray-600">{e.origin} → {e.destination}</td>
                      <td className="py-3 px-4">{fmtDate(e.service_date)}</td>
                      <td className="py-3 px-4">{money(e.revenue)}</td>
                      <td className="py-3 px-4 text-gray-600">{e.payment_status}</td>
                      <td className="py-3 px-4"><StatusBadge tone={STATUS_TONE[e.status] || 'neutral'}>{e.status}</StatusBadge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100" data-testid="ophist-cards">
              {entries.map((e) => (
                <div key={`${e.type}-${e.id}`} className="p-4" data-testid={`ophist-card-${e.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 break-words">{e.reference}</div>
                      <div className="text-sm text-gray-500 break-words">{e.customer}</div>
                    </div>
                    <StatusBadge tone={STATUS_TONE[e.status] || 'neutral'}>{e.status}</StatusBadge>
                  </div>
                  <div className="mt-2 text-sm text-gray-600 break-words">{e.origin} → {e.destination}</div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div><span className="text-gray-400">Type:</span> {e.type === 'load' ? 'Load' : 'Job'}</div>
                    <div><span className="text-gray-400">Date:</span> {fmtDate(e.service_date)}</div>
                    <div><span className="text-gray-400">Revenue:</span> {money(e.revenue)}</div>
                    <div><span className="text-gray-400">Payment:</span> {e.payment_status}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
